import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ComicCard from '@/components/feature/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { useFavorites } from '@/hooks/useFavorites';
import {
  buildChapters,
  categoryName,
  formatCount,
  getComicById,
  getRelatedComics,
} from '@/utils/comic';
import ChapterList from './components/ChapterList';

export default function DetailPage() {
  const { comicId = '' } = useParams();
  const comic = getComicById(comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSEO(
    comic ? `${comic.title}-韩漫漫画详情与章节` : '漫画详情-韩漫网址',
    comic
      ? `${comic.title}：${comic.description.slice(0, 80)}… 作者 ${comic.author}，${comic.status}，最新章节更新至第${comic.chapterCount}话。`
      : '漫画详情页',
    comic ? `${comic.title},${comic.author},韩漫,漫画详情,章节列表` : '韩漫,漫画详情',
  );

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (!comic) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-950">找不到这本漫画</h1>
          <p className="mt-2 text-sm text-foreground-500">该作品可能已下架，或链接有误</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
            <Link
              to="/category/all"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
            >
              浏览全部漫画
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const chapters = buildChapters(comic);
  const related = getRelatedComics(comic, 5);
  const favored = isFavorite(comic.id);

  const handleFavorite = () => {
    const nowFav = toggleFavorite(comic.id);
    setToast(nowFav ? '已加入我的收藏' : '已取消收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-4 pt-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link
            to={`/category/${comic.categories[0]}`}
            className="transition-colors hover:text-primary-600"
          >
            {categoryName(comic.categories[0])}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="truncate text-foreground-800">{comic.title}</span>
        </nav>

        <div className="mt-5 flex flex-col gap-6 lg:flex-row">
          <div className="w-full shrink-0 sm:w-[220px]">
            <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src={comic.cover}
                alt={`${comic.title} 漫画封面`}
                title={`${comic.title} 韩漫在线阅读`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{comic.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-800'
                    : 'bg-secondary-100 text-secondary-800'
                }`}
              >
                {comic.status}
              </span>
              {comic.categories.map((k) => (
                <Link
                  key={k}
                  to={`/category/${k}`}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                >
                  {categoryName(k)}
                </Link>
              ))}
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700">
                {comic.region}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">作者</p>
                <p className="mt-0.5 truncate text-sm font-medium text-foreground-900">
                  {comic.author}
                </p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">浏览量</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">
                  {formatCount(comic.views)}
                </p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">收藏数</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">
                  {formatCount(comic.favorites)}
                </p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">最近更新</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">{comic.updatedAt}</p>
              </div>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-foreground-600">{comic.description}</p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/read/${comic.id}/ch-${comic.chapterCount}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-book-open-line" />
                开始阅读
              </Link>
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                  favored
                    ? 'border-accent-400 bg-accent-100 text-accent-800'
                    : 'border-background-300 text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={favored ? 'ri-heart-fill' : 'ri-heart-line'} />
                {favored ? '已收藏' : '加入收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
            </div>
            <p className="mt-3 text-xs text-foreground-400">
              提示：收藏功能使用浏览器本地存储实现，仅保存在当前设备。
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <ChapterList comicId={comic.id} chapters={chapters} />
      </div>

      {related.length > 0 && (
        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="mb-6 flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 animate-fade-in">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}